import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { API_URL } from '../../config';
import PrimeDataFusionBoard from '../../components/PrimeDataFusionBoard';
import { Building2, Users, Loader2, Bot, Database, ArrowRight, ArrowLeft, CheckCircle, Sparkles, Plus, X, Tag } from 'lucide-react';

// Tag Input Component
const TagInput = ({ label, placeholder, tags, setTags, icon: Icon }) => {
  const [input, setInput] = useState('');

  const addTag = (e) => {
    if (e.key === 'Enter' && input.trim() !== '') {
      e.preventDefault();
      if (!tags.includes(input.trim())) {
        setTags([...tags, input.trim()]);
      }
      setInput('');
    }
  };

  const removeTag = (index) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  return (
    <div className="mb-6">
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
        {Icon && <Icon className="w-4 h-4 text-blue-500" />} {label}
      </label>
      <div className="p-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 flex flex-wrap gap-2 items-center focus-within:ring-2 focus-within:ring-blue-500 transition-all">
        {tags.map((tag, idx) => (
          <span key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium border border-blue-100 dark:border-blue-800">
            {tag}
            <button type="button" onClick={() => removeTag(idx)} className="text-blue-400 hover:text-blue-600 dark:hover:text-blue-200">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={addTag}
          placeholder={tags.length === 0 ? placeholder : "继续输入，按回车添加..."}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400"
        />
      </div>
    </div>
  );
};

export default function PrimeSetupWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');

  useEffect(() => {
    if (editId) {
      fetchProjectData(editId);
    }
  }, [editId]);

  const fetchProjectData = async (id) => {
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_URL}/brand-projects/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        const p = data.data;
        
        let cats = [];
        let nds = [];
        let scens = [];
        let buds = [];
        let bp = [];
        let cp = [];
        
        if (p.PromptResearchSeed && p.PromptResearchSeed.length > 0) {
           cats = p.PromptResearchSeed.find(s => s.name === '__DIM_CATEGORY__')?.keywords.split(',').filter(Boolean) || [];
           nds = p.PromptResearchSeed.find(s => s.name === '__DIM_NEEDS__')?.keywords.split(',').filter(Boolean) || [];
           scens = p.PromptResearchSeed.find(s => s.name === '__DIM_SCENARIOS__')?.keywords.split(',').filter(Boolean) || [];
           buds = p.PromptResearchSeed.find(s => s.name === '__DIM_BUDGETS__')?.keywords.split(',').filter(Boolean) || [];
           bp = p.PromptResearchSeed.find(s => s.name === '__DIM_BRAND_PRODUCTS__')?.keywords.split(',').filter(Boolean) || [];
           cp = p.PromptResearchSeed.find(s => s.name === '__DIM_COMP_PRODUCTS__')?.keywords.split(',').filter(Boolean) || [];
        }

        const maxPairs = Math.max(bp.length, cp.length, 1);
        const pairs = Array.from({length: maxPairs}).map((_, i) => ({
            id: Date.now() + i,
            brandProduct: bp[i] || '',
            compProduct: cp[i] || ''
        }));

        setFormData({
          brandName: p.name || '',
          competitors: p.competitors && p.competitors.length > 0 ? p.competitors.map(c => c.name) : [''],
          categories: cats,
          needs: nds,
          scenarios: scens,
          budgets: buds,
          productPairs: pairs,
          brandProducts: bp,
          competitorProducts: cp,
          personas: p.personas && p.personas.length > 0 ? p.personas.map(per => per.name) : ['']
        });
      }
    } catch(e) {
      console.error(e);
    }
  };

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // New 6-Dimension Form State
  const [formData, setFormData] = useState({
    brandName: '',
    competitors: [],
    productPairs: [{ id: 1, brandProduct: '', compProduct: '' }],
    brandProducts: [], // Kept for backwards compatibility if needed
    competitorProducts: [], // Kept for backwards compatibility
    personas: [],
    categories: [],
    needs: [],
    scenarios: [],
    budgets: []
  });

  const addProductPair = () => {
    setFormData(prev => ({
      ...prev,
      productPairs: [...prev.productPairs, { id: Date.now(), brandProduct: '', compProduct: '' }]
    }));
  };

  const updateProductPair = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      productPairs: prev.productPairs.map(p => p.id === id ? { ...p, [field]: value } : p)
    }));
  };

  const removeProductPair = (id) => {
    setFormData(prev => ({
      ...prev,
      productPairs: prev.productPairs.filter(p => p.id !== id)
    }));
  };

  
  // AI Loading States
  const [isGeneratingComps, setIsGeneratingComps] = useState(false);
  const [isGeneratingProducts, setIsGeneratingProducts] = useState(false);
  const [compCount, setCompCount] = useState(3);
  
  const [isGeneratingPersonas, setIsGeneratingPersonas] = useState(false);
  const [suggestedPersonas, setSuggestedPersonas] = useState([]);

  const [isGeneratingDimensions, setIsGeneratingDimensions] = useState(false);

  
  const handleAiGenerateComps = async () => {
    if (!formData.brandName) return;
    setIsGeneratingComps(true);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_URL}/seo/config/geo-ai-suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ brandName: formData.brandName, type: 'comps' })
      });
      const data = await res.json();
      if (data && data.competitors) {
        setFormData(prev => ({
          ...prev,
          competitors: data.competitors.slice(0, compCount)
        }));
      }
    } catch (e) {
      console.error(e);
      alert('AI 探测竞品失败');
    } finally {
      setIsGeneratingComps(false);
    }
  };

  
  const handleAiGenerateProducts = async () => {
    if (!formData.brandName) return;
    setIsGeneratingProducts(true);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_URL}/seo/config/geo-ai-suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          brandName: formData.brandName, 
          type: 'products',
          currentComps: formData.competitors
        })
      });
      const data = await res.json();
      if (data && data.brandProducts) {
        // Zip the two arrays into row pairs
        const maxLen = Math.max(data.brandProducts.length, (data.competitorProducts || []).length);
        const newPairs = [];
        for (let i = 0; i < maxLen; i++) {
          newPairs.push({
            id: Date.now() + i,
            brandProduct: data.brandProducts[i] || '',
            compProduct: (data.competitorProducts || [])[i] || ''
          });
        }
        setFormData(prev => ({
          ...prev,
          productPairs: newPairs.length > 0 ? newPairs : [{ id: Date.now(), brandProduct: '', compProduct: '' }],
          brandProducts: data.brandProducts,
          competitorProducts: data.competitorProducts || []
        }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsGeneratingProducts(false);
    }
  };

  const handleAiGeneratePersonas = async () => {
    if (!formData.brandName) return;
    setIsGeneratingPersonas(true);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_URL}/seo/config/geo-ai-suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ brandName: formData.brandName, type: 'personas' })
      });
      const data = await res.json();
      if (data && data.personas) {
        setSuggestedPersonas(data.personas);
        setFormData(prev => ({
          ...prev,
          personas: prev.personas.length === 0 ? data.personas : prev.personas
        }));
      }
    } catch (e) {
      console.error(e);
      alert('AI 生成人设失败');
    } finally {
      setIsGeneratingPersonas(false);
    }
  };

  const togglePersonaSelection = (item) => {
    setFormData(prev => {
      const exists = prev.personas.includes(item);
      if (exists) {
        return { ...prev, personas: prev.personas.filter(i => i !== item) };
      } else {
        return { ...prev, personas: [...prev.personas, item] };
      }
    });
  };

  const handleAiGenerateDimensions = async () => {
    if (!formData.brandName) return;
    setIsGeneratingDimensions(true);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`${API_URL}/seo/config/geo-ai-suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          brandName: formData.brandName, 
          competitors: formData.competitors,
          type: 'prime-dimensions' 
        })
      });
      const data = await res.json();
      if (data) {
        setFormData(prev => ({
          ...prev,
          categories: prev.categories.length === 0 ? (data.categories || []) : prev.categories,
          needs: prev.needs.length === 0 ? (data.needs || []) : prev.needs,
          scenarios: prev.scenarios.length === 0 ? (data.scenarios || []) : prev.scenarios,
          budgets: prev.budgets.length === 0 ? (data.budgets || []) : prev.budgets
        }));
      }
    } catch (e) {
      console.error(e);
      alert('AI 推演失败');
    } finally {
      setIsGeneratingDimensions(false);
    }
  };

  const handleNext = () => setStep(prev => Math.min(prev + 1, 4));
  const handlePrev = () => setStep(prev => Math.max(prev - 1, 1));

  const handleFinish = async (finalData = formData) => {
    if (!formData.brandName) {
      alert('请填写主推品牌');
      return;
    }
    setIsSubmitting(true);
    try {
      // Unpack productPairs back to flat strings for backend mapping
      const finalBrandProducts = formData.productPairs.map(p => p.brandProduct).filter(Boolean);
      const finalCompProducts = formData.productPairs.map(p => p.compProduct).filter(Boolean);

      // Map 4 dimensions to backend's researchSeeds field as serialized keywords
      const mappedSeeds = [
        { name: '__DIM_CATEGORY__', keywords: formData.categories.join(',') },
        { name: '__DIM_NEEDS__', keywords: formData.needs.join(',') },
        { name: '__DIM_SCENARIOS__', keywords: formData.scenarios.join(',') },
        { name: '__DIM_BUDGETS__', keywords: formData.budgets.join(',') },
        { name: '__DIM_BRAND_PRODUCTS__', keywords: finalBrandProducts.join(',') },
        { name: '__DIM_COMP_PRODUCTS__', keywords: finalCompProducts.join(',') }
      ].filter(dim => dim.keywords.length > 0);

      const payload = {
        name: formData.brandName,
        system: 'geo',
        competitors: formData.competitors.filter(c => c.trim() !== '').map(c => ({ name: c, aliases: '' })),
        researchSeeds: mappedSeeds,
        personas: formData.personas.map(p => ({ 
          name: p,
          prompt: `我的情况是：${p}`
        }))
      };

      const token = localStorage.getItem('token') || '';
      
      const res = await fetch(`${API_URL}/brand-projects${editId ? '/' + editId : ''}`, {
        method: editId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) throw new Error('网络请求失败');
      
      navigate('/prime/projects');
    } catch (e) {
      console.error(e);
      alert('初始化战略失败，请检查网络');
    } finally {
      setIsSubmitting(false);
    }
  };

  const StepIndicator = () => (
    <div className={`flex items-center justify-center ${step === 4 ? "mb-4" : "mb-12"}`}>
      {[
        { num: 1, title: '确立坐标', icon: Building2 },
        { num: 2, title: '描摹灵魂', icon: Users },
        { num: 3, title: '填充弹药', icon: Database },
        { num: 4, title: '提纯融合', icon: Sparkles }
      ].map((s, i) => (
        <React.Fragment key={s.num}>
          <div className={`flex flex-col items-center ${step >= s.num ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'}`}>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${step >= s.num ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30' : 'border-slate-300 dark:border-slate-700'} transition-colors duration-300 shadow-sm`}>
              <s.icon className="w-5 h-5" />
            </div>
            <span className={`text-xs font-bold mt-2 ${step >= s.num ? "text-blue-700 dark:text-blue-300" : "text-slate-500 dark:text-slate-300"}`}>{s.title}</span>
          </div>
          {i < 3 && (
            <div className={`w-24 h-1 mx-4 rounded-full ${step > s.num ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'} transition-colors duration-300`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-[85vh] flex flex-col items-center justify-center py-12 px-4">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white flex items-center justify-center gap-3">
            <Sparkles className="w-8 h-8 text-blue-500" />
            PRIME 战略配置向导
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            完成 6 大维度建模，构建全息意图矩阵
          </p>
        </div>

        <StepIndicator />

        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl overflow-hidden border border-slate-100 dark:border-slate-700">
          {step < 4 && (<div className="p-8 sm:p-12 min-h-[400px]">
            
            {/* Step 1: 品牌与竞品 */}
            {step === 1 && (
              <div className="animate-fade-in-up">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
                  <span className="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 px-3 py-1 rounded-lg text-sm">Dimension 1 & 2</span>
                  品牌与核心竞品
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">主推品牌 (Brand)</label>
                    <input 
                      type="text" 
                      placeholder="例：海蓝之谜 / 理想汽车" 
                      className="w-full p-4 border border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all shadow-sm"
                      value={formData.brandName}
                      onChange={e => setFormData({...formData, brandName: e.target.value})}
                    />
                  </div>
                  
                  <TagInput 
                    label="核心竞品 (Competitors)" 
                    placeholder="输入竞品名称按回车（例：赫莲娜、雅诗兰黛）"
                    tags={formData.competitors}
                    setTags={(tags) => setFormData({...formData, competitors: tags})}
                    icon={Building2}
                  />

                  <div className="flex items-center gap-3 mt-4">
                    <button
                      type="button"
                      onClick={handleAiGenerateComps}
                      disabled={isGeneratingComps || !formData.brandName}
                      className="px-6 py-3.5 h-14 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-xl font-medium text-base transition-all shadow-md flex items-center gap-2 disabled:opacity-50"
                    >
                      {isGeneratingComps ? <Loader2 className="w-5 h-5 animate-spin" /> : <Bot className="w-5 h-5" />}
                      {isGeneratingComps ? 'AI 深度探测中...' : 'AI 探测核心竞品'}
                    </button>
                    <select
                      value={compCount}
                      onChange={e => setCompCount(Number(e.target.value))}
                      className="px-4 py-3.5 h-14 w-64 min-w-[240px] border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-base outline-none cursor-pointer shadow-sm" style={{ minWidth: "240px", minHeight: "56px" }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                        <option key={num} value={num} className="text-slate-900 dark:text-white bg-white dark:bg-slate-800">
                          探测 {num} 家
                        </option>
                      ))}
                    </select>
                  </div>

                </div>

                {/* 🎯 核心单品与对标矩阵 (Slides in after brandName is set) */}
                {formData.brandName && (
                  <div className="mt-10 relative animate-fade-in-up">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 rounded-2xl blur opacity-40 animate-pulse"></div>
                    <div className="relative bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-100 dark:border-slate-700 shadow-xl">
                      <div className="flex justify-between items-center mb-6">
                        <h4 className="text-lg font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-blue-500 flex items-center gap-2">
                          <span>🎯 核心单品与对标矩阵</span>
                        </h4>
                        <button 
                          onClick={handleAiGenerateProducts}
                          disabled={isGeneratingProducts || formData.competitors.length === 0}
                          className="px-4 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-xl text-sm font-bold flex items-center gap-2 transition-transform hover:scale-105 shadow-md disabled:opacity-50 disabled:hover:scale-100"
                        >
                          {isGeneratingProducts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
                          AI 提取对标单品
                        </button>
                      </div>
                      
                      <div className="mt-4 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-slate-50 dark:bg-slate-900/50">
                        {/* Table Header */}
                        <div className="flex bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-500 dark:text-slate-400 py-3 px-4">
                          <div className="flex-1">【本品】当家单品/生态位</div>
                          <div className="flex-1">【核心竞品】对标单品</div>
                          <div className="w-16 text-center">操作</div>
                        </div>
                        
                        {/* Table Body */}
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                          {formData.productPairs.map((pair, idx) => (
                            <div key={pair.id} className="flex items-center gap-4 p-3 hover:bg-white dark:hover:bg-slate-800 transition-colors group">
                              <div className="flex-1">
                                <input
                                  type="text"
                                  value={pair.brandProduct}
                                  onChange={e => updateProductPair(pair.id, 'brandProduct', e.target.value)}
                                  placeholder="如：修护精粹水"
                                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-slate-700 dark:text-slate-200"
                                />
                              </div>
                              <div className="flex-1">
                                <input
                                  type="text"
                                  value={pair.compProduct}
                                  onChange={e => updateProductPair(pair.id, 'compProduct', e.target.value)}
                                  placeholder="如：赫莲娜黑绷带"
                                  className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-slate-700 dark:text-slate-200"
                                />
                              </div>
                              <div className="w-16 flex justify-center">
                                <button
                                  type="button"
                                  onClick={() => removeProductPair(pair.id)}
                                  className="text-slate-300 hover:text-red-500 dark:text-slate-600 dark:hover:text-red-400 p-2 rounded-lg transition-colors"
                                  title="移除该对标组"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Add Row Button */}
                        <div className="p-3 bg-white dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
                          <button
                            type="button"
                            onClick={addProductPair}
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                          >
                            <Plus className="w-4 h-4" /> 新增对标生态位
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-xs text-slate-400 mt-4">提示：必须先在上方确认至少一个核心竞品，AI 才能推演准确的对标单品矩阵。如果您对 AI 生成的结果不满意，可以直接在表格中修改或手动增删行。</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: 受众人设 */}
            {step === 2 && (
              <div className="animate-fade-in-up">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <span className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 px-3 py-1 rounded-lg text-sm">Dimension 3</span>
                      受众人设 (Persona)
                    </h3>
                    <p className="text-slate-500 text-sm mt-2">精确定义目标受众，引擎在执行全网侦察时将自动代入这些身份视角。</p>
                  </div>
                  <button 
                    onClick={handleAiGeneratePersonas}
                    disabled={isGeneratingPersonas}
                    className="flex items-center gap-2 bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400 dark:hover:bg-purple-900/50 border border-purple-200 dark:border-purple-800 px-5 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm"
                  >
                    {isGeneratingPersonas ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
                    AI 推演核心受众
                  </button>
                </div>

                {/* AI Suggestions Box */}
                {suggestedPersonas.length > 0 && (
                  <div className="mb-6 p-5 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl">
                    <label className="block text-xs font-bold text-slate-500 dark:text-slate-300 mb-4 flex items-center gap-1"><Sparkles className="w-4 h-4 text-amber-500"/> AI 洞察受众 (点击可勾选加入)</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {suggestedPersonas.map((persona, idx) => {
                        const isSelected = formData.personas.includes(persona);
                        if (isSelected) return null; // Hide if already selected to save space
                        return (
                          <button 
                            key={idx}
                            onClick={() => togglePersonaSelection(persona)}
                            className="text-left px-4 py-3 rounded-xl text-sm border transition-all flex items-center justify-between bg-white hover:border-purple-400 border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 hover:shadow-sm"
                          >
                            <span>{persona}</span>
                            <Plus className="w-4 h-4 text-slate-400" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <TagInput 
                  label="人群标签" 
                  placeholder="例：干敏皮贵妇、医美后修护人群、熬夜打工人..."
                  tags={formData.personas}
                  setTags={(tags) => setFormData({...formData, personas: tags})}
                  icon={Users}
                />
              </div>
            )}

            {/* Step 3: 填充弹药 (4大细分维度) */}
            {step === 3 && (
              <div className="animate-fade-in-up">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <span className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 px-3 py-1 rounded-lg text-sm">Dimension 4-6</span>
                    精细化意图弹药库
                  </h3>
                  <button 
                    onClick={handleAiGenerateDimensions}
                    disabled={isGeneratingDimensions}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800 rounded-xl hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors font-medium text-sm disabled:opacity-50"
                  >
                    {isGeneratingDimensions ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {isGeneratingDimensions ? '贾维斯正在推演中...' : 'AI智能推演'}
                  </button>
                </div>
                <p className="text-slate-500 text-sm mb-8">完善最后四大决策维度，系统将自动执行笛卡尔积运算，生成全息意图矩阵。</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <TagInput 
                    label="品类 (Category)" 
                    placeholder="例：面霜、眼霜、精华..."
                    tags={formData.categories}
                    setTags={(tags) => setFormData({...formData, categories: tags})}
                    icon={Tag}
                  />
                  <TagInput 
                    label="核心需求 (Needs)" 
                    placeholder="例：抗老、修护、美白..."
                    tags={formData.needs}
                    setTags={(tags) => setFormData({...formData, needs: tags})}
                    icon={Tag}
                  />
                  <TagInput 
                    label="使用场景 (Scenario)" 
                    placeholder="例：换季过敏、送丈母娘..."
                    tags={formData.scenarios}
                    setTags={(tags) => setFormData({...formData, scenarios: tags})}
                    icon={Tag}
                  />
                  <TagInput 
                    label="预算分层 (Budget)" 
                    placeholder="例：3000元以上、百元平替..."
                    tags={formData.budgets}
                    setTags={(tags) => setFormData({...formData, budgets: tags})}
                    icon={Tag}
                  />
                </div>
              </div>
            )}

          </div>)}


            {/* Step 4: 数据融合 */}
            {step === 4 && (
              <div className="animate-fade-in-up">
                <PrimeDataFusionBoard 
                  initialCategories={formData.categories}
                  initialNeeds={formData.needs}
                  onComplete={(mergedData) => {
                    const finalData = {
                      ...formData,
                      categories: mergedData.categories,
                      needs: mergedData.needs
                    };
                    setFormData(finalData);
                    handleFinish(finalData);
                  }}
                  onBack={() => setStep(3)}
                />
              </div>
            )}
          {/* Footer Navigation */}
          {step < 4 && (
          <div className="bg-slate-50 dark:bg-slate-900 px-8 py-5 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center">
            {step > 1 ? (
              <button 
                onClick={handlePrev}
                disabled={isSubmitting}
                className="px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
              >
                <ArrowLeft className="w-4 h-4" /> 返回上一步
              </button>
            ) : (
              <div aria-hidden="true" className="px-5 py-2.5"></div>
            )}

            {step < 4 ? (
              <button 
                onClick={handleNext}
                disabled={step === 1 && !formData.brandName}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white rounded-xl font-bold flex items-center gap-2 shadow-md transition-all"
              >
                下一步推演 <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button 
                onClick={handleFinish}
                disabled={isSubmitting}
                className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-bold flex items-center gap-2 shadow-lg transition-all transform hover:scale-105"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2"><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> 初始化矩阵中...</span>
                ) : (
                  <span className="flex items-center gap-2"><CheckCircle className="w-6 h-6" /> 生成意图作战网格</span>
                )}
              </button>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
